import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mic2, Play, Trash2, RotateCcw, Copy, Download, Loader2, CheckCircle2, XCircle, Cpu, Music2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  queued: { label: "Queued", color: "#a1a1aa" },
  downloading: { label: "Fetching", color: "#60a5fa" },
  separating: { label: "Splitting vocals", color: "#38bdf8" },
  converting: { label: "Singing", color: "#f472b6" },
  mixing: { label: "Mixing", color: "#c084fc" },
  uploading: { label: "Uploading", color: "#facc15" },
  ready: { label: "Ready", color: "#4ade80" },
  error: { label: "Failed", color: "#f87171" },
  cancelled: { label: "Cancelled", color: "#71717a" },
};
const ACTIVE = ["downloading", "separating", "converting", "mixing", "uploading"];

export default function Sing() {
  const [cfg, setCfg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [url, setUrl] = useState("");
  const [pitch, setPitch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, j] = await Promise.all([api.get("/sing/config"), api.get("/sing/jobs")]);
      setCfg(c.data); setJobs(j.data);
    } catch (e) {}
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);

  const saveCfg = async (patch) => {
    try { setCfg((await api.put("/sing/config", patch)).data); } catch (e) { toast.error("Could not save"); }
  };
  const queue = async () => {
    if (!/^https?:\/\//.test(url.trim())) { toast.error("Paste a song link first"); return; }
    setBusy(true);
    try {
      await api.post("/sing/jobs", { url: url.trim(), pitch: pitch === "" ? null : parseInt(pitch) });
      setUrl(""); toast.success("Song queued — Pancake is warming up"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not queue"); } finally { setBusy(false); }
  };
  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const copyToken = () => { navigator.clipboard.writeText(cfg.worker_token); toast.success("Worker token copied"); };
  const download = async () => {
    try {
      const r = await api.get("/sing/worker-download", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "pancake-voice-worker.zip"; a.click();
    } catch (e) { toast.error("Download failed"); }
  };

  const w = cfg?.worker || {};
  return (
    <div className="space-y-6" data-testid="sing-page">
      <div>
        <h2 className="text-xl font-heading font-800 flex items-center gap-2"><Mic2 className="w-5 h-5 text-primary" />Sing</h2>
        <p className="text-sm text-muted-foreground mt-1">Paste a song and PancakeCupCake_ performs an AI cover on the overlay. Mods can also use <code className="text-primary">!sing &lt;link&gt;</code> in chat.</p>
      </div>

      <div className={`rounded-lg p-4 border bg-[#0a0a0a] flex flex-wrap items-center gap-3 ${w.online ? "border-green-500/40" : "border-white/10"}`} data-testid="sing-worker-box">
        <Cpu className={`w-5 h-5 ${w.online ? "text-green-400" : "text-muted-foreground"}`} />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-medium" data-testid="sing-worker-status">
            {w.online ? <span className="text-green-400">Voice worker online</span> : <span className="text-muted-foreground">Voice worker offline</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            {w.name ? <>{w.name} · {w.gpu} · model <b className="text-foreground">{w.model}</b> · {w.backend}</> : "Run the worker on your GPU PC to start singing. Songs queue up until it's online."}
          </div>
        </div>
        <Button variant="outline" size="sm" className="border-white/10 h-8" onClick={download} data-testid="sing-worker-download"><Download className="w-4 h-4 mr-1" />Download worker</Button>
      </div>

      <div className="rounded-lg p-4 border border-white/10 bg-[#0a0a0a] space-y-3">
        <Label className="text-xs">Worker token <span className="text-muted-foreground">(paste into config.env as WORKER_TOKEN)</span></Label>
        <div className="flex gap-2">
          <Input readOnly value={cfg?.worker_token || ""} className="bg-secondary border-white/10 font-mono text-xs" data-testid="sing-worker-token" />
          <Button variant="outline" className="border-white/10" onClick={copyToken} data-testid="sing-token-copy"><Copy className="w-4 h-4" /></Button>
          <Button variant="ghost" className="text-muted-foreground" onClick={() => act(() => api.post("/sing/config/regenerate-token"), "New token generated — update config.env")} data-testid="sing-token-regen"><RotateCcw className="w-4 h-4" /></Button>
        </div>
        <div className="flex flex-wrap gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!cfg?.auto_play} onCheckedChange={(v) => saveCfg({ auto_play: v })} data-testid="sing-auto-play" />Play on overlay as soon as it's ready</label>
          <label className="flex items-center gap-2 text-sm"><Switch checked={!!cfg?.announce} onCheckedChange={(v) => saveCfg({ announce: v })} data-testid="sing-announce" />Announce in Twitch chat</label>
          <label className="flex items-center gap-2 text-sm">Default pitch
            <Input type="number" min={-24} max={24} value={cfg?.default_pitch ?? 0} onChange={(e) => saveCfg({ default_pitch: parseInt(e.target.value) || 0 })} className="w-20 h-8 bg-secondary border-white/10" data-testid="sing-default-pitch" /> st
          </label>
          <label className="flex items-center gap-2 text-sm">Max length
            <Input type="number" min={30} max={1800} value={cfg?.max_duration ?? 480} onChange={(e) => saveCfg({ max_duration: parseInt(e.target.value) || 480 })} className="w-24 h-8 bg-secondary border-white/10" data-testid="sing-max-duration" /> sec
          </label>
        </div>
      </div>

      <div className="rounded-lg p-4 border border-white/10 bg-[#0a0a0a] flex flex-wrap gap-2 items-center">
        <Music2 className="w-4 h-4 text-primary" />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="YouTube link or direct audio URL" className="bg-secondary border-white/10 flex-1 min-w-[240px]" data-testid="sing-url-input" onKeyDown={(e) => e.key === "Enter" && queue()} />
        <Input type="number" min={-24} max={24} value={pitch} onChange={(e) => setPitch(e.target.value)} placeholder="pitch" className="w-20 bg-secondary border-white/10" data-testid="sing-pitch-input" />
        <Button onClick={queue} disabled={busy} data-testid="sing-queue-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic2 className="w-4 h-4 mr-1" />}Queue song</Button>
      </div>

      <div className="space-y-2" data-testid="sing-jobs">
        {jobs.length === 0 && <div className="text-sm text-muted-foreground px-1">No songs yet — queue one above.</div>}
        {jobs.map((j, i) => <JobRow key={j.id} j={j} i={i} act={act} />)}
      </div>
    </div>
  );
}

function JobRow({ j, i, act }) {
  const s = STATUS[j.status] || { label: j.status, color: "#fff" };
  const active = ACTIVE.includes(j.status);
  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-3" data-testid={`sing-job-${i}`}>
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-800 tracking-widest px-2 py-0.5 rounded-full shrink-0" style={{ color: s.color, border: `1px solid ${s.color}66`, background: `${s.color}14` }} data-testid={`sing-job-status-${i}`}>{s.label.toUpperCase()}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate" title={j.url}>{j.title || j.url}</div>
          <div className="text-xs text-muted-foreground truncate">by {j.requested_by}{j.pitch ? ` · pitch ${j.pitch > 0 ? "+" : ""}${j.pitch}` : ""}{j.status === "error" ? ` · ${j.error}` : active || j.status === "queued" ? ` · ${j.message}` : ""}{j.played ? " · played" : ""}</div>
        </div>
        {j.status === "ready" && <a href={`${API}${j.result_url.replace(/^\/api/, "")}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0" data-testid={`sing-job-open-${i}`}><ExternalLink className="w-3.5 h-3.5" />Open audio</a>}
        {j.status === "ready" && <Button size="sm" variant="outline" className="border-white/10 h-8" onClick={() => act(() => api.post(`/sing/jobs/${j.id}/play`), "Playing on the overlay")} data-testid={`sing-job-play-${i}`}><Play className="w-4 h-4 mr-1" />Play</Button>}
        {j.status === "error" && <Button size="sm" variant="outline" className="border-white/10 h-8" onClick={() => act(() => api.post(`/sing/jobs/${j.id}/retry`), "Re-queued")} data-testid={`sing-job-retry-${i}`}><RotateCcw className="w-4 h-4 mr-1" />Retry</Button>}
        {j.status === "ready" ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : j.status === "error" ? <XCircle className="w-4 h-4 text-red-400" /> : active ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : null}
        <Button size="icon" variant="ghost" className="text-muted-foreground hover:text-red-400 h-8 w-8" onClick={() => act(() => api.delete(`/sing/jobs/${j.id}`))} data-testid={`sing-job-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
      {(active || j.status === "queued") && (
        <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${j.progress || 0}%`, background: s.color }} /></div>
      )}
    </div>
  );
}
